import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Save, Eye, X, ArrowLeft } from 'lucide-react';
import { ShowJsonButton } from '../../components/ShowJsonButton';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormField } from '../../types/form';
import { FieldBuilder } from './components/FieldBuilder';
import { DynamicField } from './components/DynamicField';
import { generateSlugFromTitle } from './utils/slugGenerator';
import { validateFormPayload, logPayloadStructure } from './utils/payloadValidator';
import { formsApi } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
import { useFormById } from './hooks/useFormById';
import LoadingCard from '../../components/LoadingCard';
import { useTheme } from '../../context/ThemeContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableFieldProps {
  field: FormField;
  onUpdate: (field: FormField) => void;
  onDelete: () => void;
  allFields?: FormField[];
}

const SortableField: React.FC<SortableFieldProps> = ({ field, onUpdate, onDelete, allFields }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <FieldBuilder 
        field={field} 
        onUpdate={onUpdate} 
        onDelete={onDelete} 
        isDragging={isDragging}
        dragHandleListeners={listeners}
        allFields={allFields}
      />
    </div>
  );
};

const FormBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formId = id ? parseInt(id) : null;
  
  const { form: existingForm, loading: loadingForm } = useFormById(formId);
  
  const [formSchema, setFormSchema] = useState<{
    title: string;
    description: string;
    handler: string;
    fields: FormField[];
    medical_category: string;
  }>({
    title: '',
    description: '',
    handler: '',
    fields: [],
    medical_category: '',
  });

  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load existing form data when editing
  useEffect(() => {
    if (existingForm) {
      setFormSchema({
        title: existingForm.title,
        description: existingForm.description || '',
        handler: existingForm.handler || '',
        fields: (existingForm.fields || []).map(f => ({
          id: f.id,
          type: f.type,
          label: f.label,
          name: f.name,
          placeholder: f.placeholder || '',
          defaultValue: f.defaultValue || '',
          required: f.required || false,
          order: f.order,
          validation: f.validation || {},
          options: f.options || [],
          conditionalLogic: f.conditionalLogic || undefined,
          calculation: f.calculation || undefined,
          helpText: f.helpText || '',
          width: f.width || 'full',
          hidden: f.hidden || false,
        })),
        medical_category: (existingForm as any).medical_category || '',
      });
    }
  }, [existingForm]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddField = () => {
    const newField: FormField = {
      id: uuidv4(),
      type: 'text',
      label: 'New Field',
      name: `field_${Date.now()}`,
      placeholder: '',
      defaultValue: '',
      required: false,
      order: formSchema.fields.length,
      validation: {},
      width: 'full',
      helpText: '',
    };

    setFormSchema({
      ...formSchema,
      fields: [...formSchema.fields, newField],
    });
  };

  const handleUpdateField = (index: number, updatedField: FormField) => {
    const updatedFields = [...formSchema.fields];
    updatedFields[index] = updatedField;
    setFormSchema({ ...formSchema, fields: updatedFields });
  };

  const handleDeleteField = (index: number) => {
    const updatedFields = formSchema.fields.filter((_, i) => i !== index);
    setFormSchema({ ...formSchema, fields: updatedFields });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formSchema.fields.findIndex((f) => f.id === active.id);
      const newIndex = formSchema.fields.findIndex((f) => f.id === over.id);

      const reorderedFields = arrayMove(formSchema.fields, oldIndex, newIndex).map((field, idx) => ({
        ...field,
        order: idx,
      }));

      setFormSchema({ ...formSchema, fields: reorderedFields });
    }
  };

  const generateHandler = () => {
    if (!formSchema.title) {
      showError('Please enter a form title first');
      return;
    }
    
    try {
      const handler = generateSlugFromTitle(formSchema.title, true);
      setFormSchema({ ...formSchema, handler });
      showSuccess('Handler generated from title!');
    } catch (error: any) {
      showError(error.message || 'Failed to generate handler');
    }
  };

  const handleSave = async () => {
    if (!formSchema.title || !formSchema.handler) {
      showError('Please provide a title and handler');
      return;
    }

    if (formSchema.fields.length === 0) {
      showError('Please add at least one field');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formSchema.title,
        description: formSchema.description,
        handler: formSchema.handler,
        fields: formSchema.fields.map(f => ({
          id: f.id,
          type: f.type,
          label: f.label,
          name: f.name,
          placeholder: f.placeholder,
          defaultValue: f.defaultValue,
          required: f.required,
          order: f.order,
          options: f.options,
          validation: f.validation,
          conditionalLogic: f.conditionalLogic,
          calculation: f.calculation,
          helpText: f.helpText,
          width: f.width,
          hidden: f.hidden,
        })),
        medical_category: formSchema.medical_category,
      };

      // Validate and log payload structure
      if (!validateFormPayload(payload)) {
        showError('Invalid form data. Check console for details.');
        return;
      }
      
      logPayloadStructure(payload);

      if (formId) {
        await formsApi.update(formId, payload);
        showSuccess('Form updated successfully!');
      } else {
        await formsApi.create(payload);
        showSuccess('Form created successfully!');
      }
      
      setTimeout(() => {
        navigate('/form');
      }, 1500);
    } catch (error: any) {
      console.error('API Error:', error);
      showError(error.response?.data?.message || `Failed to ${formId ? 'update' : 'create'} form`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingForm) {
    return <LoadingCard title="Form Builder" message="Loading form..." />;
  }

  return (
    <div className="min-h-screen theme-bg-primary p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="theme-card rounded-xl shadow-sm p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <button
                onClick={() => navigate('/form')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Forms</span>
              </button>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                {formId ? 'Edit Form' : 'Form Builder'}
              </h1>
              <p className="theme-text-secondary">
                {formId ? 'Update your form configuration' : 'Create dynamic forms for lead collection'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <ShowJsonButton
                schema={formSchema}
                label="Show JSON"
              />
              <button
                onClick={() => setShowPreview(true)}
                disabled={formSchema.fields.length === 0}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                <Eye className="w-4 h-4" />
                Preview Form
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Saving...' : formId ? 'Update Form' : 'Save Form'}
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Builder */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="theme-card rounded-xl shadow-sm p-6"
            >
              <h2 className="text-xl font-semibold mb-4 theme-text-primary">Form Information</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">
                    Form Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formSchema.title}
                    onChange={(e) => setFormSchema({ ...formSchema, title: e.target.value })}
                    className="w-full px-4 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                    placeholder="Enter form title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">
                    Description
                  </label>
                  <textarea
                    value={formSchema.description}
                    onChange={(e) => setFormSchema({ ...formSchema, description: e.target.value })}
                    className="w-full px-4 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                    rows={3}
                    placeholder="Enter form description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">
                    Form Handler <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formSchema.handler}
                      onChange={(e) => setFormSchema({ ...formSchema, handler: e.target.value })}
                      className="flex-1 px-4 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                      placeholder="unique-form-handler"
                    />
                    <button
                      onClick={generateHandler}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Generate
                    </button>
                  </div>
                  <p className="text-xs theme-text-tertiary mt-1">
                    Handler: {formSchema.handler || 'your-handler'}
                  </p>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formSchema.medical_category}
                    onChange={(e) => setFormSchema({ ...formSchema, medical_category: e.target.value })}
                    className="w-full px-4 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                    placeholder="e.g., patient-intake, lead-form"
                  />
                </div> */}
              </div>
            </motion.div>

            {/* Fields */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="theme-card rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold theme-text-primary">Form Fields</h2>
                <button
                  onClick={handleAddField}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {formSchema.fields.length === 0 ? (
                <div className="text-center py-12 theme-text-tertiary">
                  <p>No fields added yet. Click "Add Field" to get started.</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={formSchema.fields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {formSchema.fields.map((field, index) => (
                        <SortableField
                          key={field.id}
                          field={field}
                          onUpdate={(updatedField) => handleUpdateField(index, updatedField)}
                          onDelete={() => handleDeleteField(index)}
                          allFields={formSchema.fields}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </motion.div>
          </div>

          {/* Settings Panel */}
          <div className="space-y-6">


            {/* Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="theme-card rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 theme-text-secondary" />
                <h2 className="text-xl font-semibold theme-text-primary">Quick Preview</h2>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="theme-text-secondary">Total Fields:</span>
                  <span className="font-medium theme-text-primary">{formSchema.fields.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="theme-text-secondary">Required Fields:</span>
                  <span className="font-medium theme-text-primary">
                    {formSchema.fields.filter((f) => f.required).length}
                  </span>
                </div>
                {formSchema.medical_category && (
                  <div className="flex justify-between">
                    <span className="theme-text-secondary">Category:</span>
                    <span className="font-medium theme-text-primary text-xs">
                      {formSchema.medical_category}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && <FormPreviewModal formSchema={formSchema} formId={formId} onClose={() => setShowPreview(false)} />}
      </AnimatePresence>
    </div>
  );
};

// Preview Modal Component
const FormPreviewModal: React.FC<{
  formSchema: { title: string; description: string; fields: FormField[] };
  formId: number | null;
  onClose: () => void;
}> = ({ formSchema, formId, onClose }) => {
  const { theme } = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const onSubmit = async (data: any) => {
    if (!formId) {
      showError('Save the form first before testing submission.');
      return;
    }

    setSubmitting(true);
    try {
      await formsApi.submit(formId, {
        data,
        metadata: {
          submittedAt: new Date().toISOString(),
          source: 'preview',
        },
      });
      showSuccess('Test submission successful! Response stored.');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="theme-card rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 theme-border-t">
          <div>
            <h2 className="text-2xl font-bold theme-text-primary">Form Preview</h2>
            <p className="text-sm theme-text-secondary mt-1">Test your form before publishing</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--hover-bg)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 theme-text-secondary" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="mb-6">
            <h3 className="text-xl font-bold theme-text-primary mb-2">{formSchema.title || 'Untitled Form'}</h3>
            {formSchema.description && (
              <p className="theme-text-secondary">{formSchema.description}</p>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex flex-wrap gap-4">
              {formSchema.fields.map((field) => (
                <DynamicField
                  key={field.id}
                  field={field}
                  register={register}
                  errors={errors}
                  value={watch(field.name)}
                  theme={theme}
                  watch={watch}
                  setValue={setValue}
                  allFields={formSchema.fields}
                />
              ))}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 theme-border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-[var(--bg-tertiary)] theme-text-primary rounded-lg hover:bg-[var(--hover-bg)] transition-colors"
              >
                Close Preview
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Submitting...</>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FormBuilderPage;
